import {FormLayout, FormActions, TextField, SelectField} from '@hconnect/common/components'
import {yupResolver} from '@hookform/resolvers/yup'
import {isUndefined} from 'lodash'
import {useMemo} from 'react'
import {FormProvider, useForm, useFormState, SubmitHandler} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useTaskUpmPlantsOptions} from '../../hooks'
import {
  TaskSource,
  TaskStatus,
  OIL_ANALYSIS_ACTION_STATUSES,
  OilAnalysisActionFormValues
} from '../../types'
import {getOilAnalysisActionValidationSchema} from '../../validators'

import {EquipmentSearchField} from './EquipmentSearchField'
import {MainEquipmentSearchField} from './MainEquipmentSearchField'
import {OilAnalysisPriorityField} from './OilAnalysisPriorityField'
import {OilAnalysisUserSearchFormField} from './OilAnalysisUserSearchFormField'
import {ProcessStageField} from './ProcessStageField'
import {RichTextFormField, MomentDatePickerFormField} from './shared'
import {TaskStatusField} from './TaskStatusField'
import {TaskTypeField} from './TaskTypeField'

type Props = {
  onSubmit: SubmitHandler<OilAnalysisActionFormValues>
  onCancel: () => void
  onTaskTypeChange: (taskType: TaskSource | undefined) => void
  initialValues?: OilAnalysisActionFormValues
  externalTaskId?: string
  timezone: string
  plantId: string
  onStatusChange?: (newValue: TaskStatus | null, resetStatus: () => void) => void
}

const defaultValues: Partial<OilAnalysisActionFormValues> = {
  title: '',
  description: '',
  plantUpmId: undefined,
  status: TaskStatus.Pending,
  assignees: []
}

export const OilAnalysisActionForm = ({initialValues, timezone, plantId, ...props}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const validationSchema = useMemo(() => getOilAnalysisActionValidationSchema({t}), [t])

  const methods = useForm<OilAnalysisActionFormValues>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    values: initialValues,
    ...(!initialValues && {defaultValues})
  })

  const mainEquipment = methods.watch('mainEquipment')

  const {isValid, isSubmitting} = useFormState({
    control: methods.control
  })

  const plantOptions = useTaskUpmPlantsOptions(TaskSource.OIL_ANALYSIS_ACTIONS)
  const plantUpmId = methods.watch('plantUpmId')

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(props.onSubmit)}>
        <FormLayout>
          <TaskTypeField
            value={TaskSource.OIL_ANALYSIS_ACTIONS}
            onChange={props.onTaskTypeChange}
          />
          <TextField name="title" label={t('task.label.title')} control={methods.control} />
          <RichTextFormField
            name="description"
            label={t('task.label.description')}
            pressAndHoldRecordLabel={t('task.label.pressAndHoldRecord')}
            control={methods.control}
          />
          {timezone && (
            <MomentDatePickerFormField
              name="dueDate"
              disableDateSwitch={true}
              label={t('task.label.dueDate')}
              control={methods.control}
              onChange={(date) => {
                if (date) {
                  methods.setValue('dueDate', date.toISOString(), {shouldValidate: true})
                }
              }}
              timezone={timezone}
            />
          )}
          <SelectField
            disabled={true}
            name="plantUpmId"
            label={t('task.label.plant')}
            control={methods.control}
            options={plantOptions}
          />
          <TaskStatusField
            name="status"
            label={t('task.label.status')}
            control={methods.control}
            allowedStatuses={OIL_ANALYSIS_ACTION_STATUSES}
            onChange={(newValue, previousValue) => {
              props.onStatusChange?.(newValue ?? null, () => {
                methods.setValue('status', previousValue as OilAnalysisActionFormValues['status'])
              })
            }}
          />
          <OilAnalysisPriorityField
            name="priority"
            label={t('task.label.priority')}
            control={methods.control}
          />
          <EquipmentSearchField
            plantId={plantId}
            name="equipment"
            label={t('task.label.equipment')}
            control={methods.control}
            onChange={(equipment) => {
              const mainEquipmentData = isUndefined(equipment)
                ? undefined
                : (methods.getValues('mainEquipment') ?? equipment?.mainEquipment)
              methods.setValue('mainEquipment', mainEquipmentData)
              methods.setValue('processStage', equipment?.processStage?.processStep)
            }}
            mainEquipmentId={mainEquipment?.id}
            optional
          />
          <MainEquipmentSearchField
            plantId={plantId}
            name="mainEquipment"
            label={t('task.label.mainEquipment')}
            control={methods.control}
            optional
          />
          <ProcessStageField
            plantId={plantId}
            name="processStage"
            label={t('task.label.processStage')}
            control={methods.control}
            optional
          />
          <OilAnalysisUserSearchFormField
            plantUpmId={plantUpmId}
            name="assignees"
            label={t('task.label.assignee')}
            pleaseStartTypingLabel={t('task.label.pleaseStartTyping')}
            control={methods.control}
            optional
          />
          <FormActions onCancel={props.onCancel} isSubmitting={isSubmitting} isValid={isValid} />
        </FormLayout>
      </form>
    </FormProvider>
  )
}
